import { IconDownloadMS, IconUploadMS } from '@/constants/icons';
import { validateFiles } from '@/helpers';
import useErrorMessage from '@/hooks/useErrorMessage';
import useSnackbar from '@/hooks/useSnackbar';
import { Box, Typography } from '@mui/material';
import { ChangeEvent, useRef, useState } from 'react';

import { StatusCode } from '../../../../api/enumerations';
import { uploadPreFlightDocument } from '../../../../api/preFlight';
import {
  PreFlightData,
  PreFlightDocumentData,
} from '../../../../api/preFlight/types';
import { Constants } from '../../constants';
import {
  ButtonBox,
  DialogContainer,
  EditButton,
  FilledButton,
  OutlinedButton,
  RegisterBox,
  StyledDialog,
  TextTypography,
} from './styles';

interface DeedUploadProps {
  preFlightId: number;
  matriculaDoc: PreFlightData['documents'][number] | undefined;
  onUploadSuccess: (doc: PreFlightDocumentData) => void;
}

export function DeedUpload({
  preFlightId,
  matriculaDoc,
  onUploadSuccess,
}: DeedUploadProps): JSX.Element {
  const [deedFile, setDeedFile] = useState<File | undefined>(undefined);
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const deedInputRef = useRef<HTMLInputElement>(null);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  const downloadFile = (link: string): void => {
    const downloadLink = document.createElement('a');
    downloadLink.href = link;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>): void => {
    if (!e.target.files?.item(0) || !validateFiles(e.target.files[0].type)) {
      handleSnackbar(Constants.fileFormatError, true);
      return;
    }
    const file = e.target.files[0];
    setDeedFile(file);
    const text = matriculaDoc
      ? Constants.confirmReplace(matriculaDoc.original_filename, file.name)
      : Constants.confirmAdd(file.name);
    setConfirmText(text);
    setOpenDialog(true);
  };

  const handleSubmit = async (): Promise<void> => {
    if (!deedFile) return;

    const formData = new FormData();
    formData.append('file', deedFile);
    formData.append('type', 'DEED');

    try {
      const response = await uploadPreFlightDocument(preFlightId, formData);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== StatusCode.OK)
        throw new Error(Constants.errorMessage);

      handleSnackbar(Constants.fileAddedSuccess, false);
      if (response.data) onUploadSuccess(response.data);
      setDeedFile(undefined);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  };

  return (
    <>
      <input
        ref={deedInputRef}
        accept="image/*,application/pdf"
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />
      <RegisterBox>
        {matriculaDoc ? (
          <>
            <Box>
              <Typography fontSize={14} fontWeight={500}>
                {Constants.registrationFile}
              </Typography>
              <Typography fontSize={11} color="text.secondary">
                {Constants.fileFormats}
              </Typography>
            </Box>
            <Box display="flex" gap="8px">
              <EditButton onClick={() => deedInputRef.current?.click()}>
                {IconUploadMS}
                {Constants.replaceFile}
              </EditButton>
              <EditButton
                onClick={() => downloadFile(matriculaDoc.download_url)}
              >
                {IconDownloadMS}
                {Constants.downloadFile}
              </EditButton>
            </Box>
          </>
        ) : (
          <>
            <Box>
              <Typography fontSize={14} fontWeight={500}>
                {Constants.registrationFile}
              </Typography>
              <Typography fontSize={11} color="text.secondary">
                {Constants.noDeedFile}
              </Typography>
            </Box>
            <Box display="flex" gap="8px">
              <EditButton onClick={() => deedInputRef.current?.click()}>
                {IconUploadMS}
                {Constants.addFile}
              </EditButton>
            </Box>
          </>
        )}
      </RegisterBox>
      <StyledDialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogContainer>
          <TextTypography>
            {Constants.confirmQuestion(confirmText)}
          </TextTypography>
          <ButtonBox>
            <OutlinedButton onClick={() => setOpenDialog(false)}>
              {Constants.no}
            </OutlinedButton>
            <FilledButton
              onClick={() => {
                handleSubmit();
                setOpenDialog(false);
              }}
            >
              {Constants.yes}
            </FilledButton>
          </ButtonBox>
        </DialogContainer>
      </StyledDialog>
    </>
  );
}
